import { useCallback, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { District, State } from "@digital-girls-hub/shared-resources/models/db";
import { services } from "../services";
import { useAuth } from "../utils/hooks/useAuth";
import { isPPAdmin, isStateAdmin } from "../utils/roles";

type LearnerRecord = {
  id: bigint;
  name: string;
  email: string | null;
  mobileNumber: string;
  gender: string;
  onboardingStatus: string;
  courseCompleted: boolean;
  placementStatus: "placed" | "not_placed";
  placementType?: "self" | "pp" | null;
  vleCscId: string;
  vleName: string;
  stateName: string;
  districtName: string;
  placementPartnerId: bigint | null;
  placementPartnerName: string;
  createdAt: string;
};

type LearnerResponse = {
  data: LearnerRecord[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalUsersCount: number;
    totalPages: number;
  };
  summary: {
    placedLearnerCount: number;
    selfPlacedLearnerCount: number;
    placedByPPLearnerCount: number;
  };
};

type Filters = {
  search: string;
  stateId: number | "";
  districtId: number | "";
  placementPartnerId: string;
  vleCscId: string;
  placementStatus: "" | "placed" | "not_placed";
  placementType: "" | "self" | "pp";
};

export function LearnerTableContent() {
  const { admin, role } = useAuth();
  const stateAdmin = isStateAdmin(role);
  const ppAdmin = isPPAdmin(role);
  const defaultStateId = admin?.stateId ? Number(admin.stateId) : "";

  const [states, setStates] = useState<State[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [placementPartners, setPlacementPartners] = useState<
    { id: string; name: string }[]
  >([]);
  const [searchForm, setSearchForm] = useState<Filters>({
    search: "",
    stateId: defaultStateId,
    districtId: "",
    placementPartnerId: "",
    vleCscId: "",
    placementStatus: "",
    placementType: "",
  });
  const [filters, setFilters] = useState<Filters>({
    search: "",
    stateId: defaultStateId,
    districtId: "",
    placementPartnerId: "",
    vleCscId: "",
    placementStatus: "",
    placementType: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState<LearnerRecord[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [summary, setSummary] = useState({
    placedLearnerCount: 0,
    selfPlacedLearnerCount: 0,
    placedByPPLearnerCount: 0,
  });
  const [isLoading, setIsLoading] = useState(false);

  const fetchLearners = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await services.requestSvc.get<LearnerResponse>("/users", {
        withCredentials: true,
        params: {
          page,
          pageSize,
          search: filters.search || undefined,
          stateId: filters.stateId || undefined,
          districtId: filters.districtId || undefined,
          placementPartnerId: filters.placementPartnerId || undefined,
          vleCscId: filters.vleCscId || undefined,
          placementStatus: filters.placementStatus || undefined,
          placementType: filters.placementType || undefined,
        },
      });
      setRows(response.data || []);
      setTotalRows(response.pagination?.totalUsersCount || 0);
      setSummary(
        response.summary || {
          placedLearnerCount: 0,
          selfPlacedLearnerCount: 0,
          placedByPPLearnerCount: 0,
        },
      );
    } catch (error: any) {
      toast.error(error?.message || "Failed to fetch learners.");
      setRows([]);
      setTotalRows(0);
      setSummary({
        placedLearnerCount: 0,
        selfPlacedLearnerCount: 0,
        placedByPPLearnerCount: 0,
      });
    } finally {
      setIsLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void services.requestSvc
      .get<State[]>("/states", { withCredentials: true })
      .then((records) => {
        if (defaultStateId) {
          setStates(
            (records || []).filter(
              (record) => Number(record.id) === Number(defaultStateId),
            ),
          );
          return;
        }
        setStates(records || []);
      })
      .catch(() => setStates([]));

    if (ppAdmin) {
      setPlacementPartners([]);
      return;
    }

    void services.requestSvc
      .get<Array<{ id: bigint; name: string }>>("/placement-partners", {
        withCredentials: true,
      })
      .then((records) => {
        setPlacementPartners(
          (records || []).map((record) => ({
            id: String(record.id),
            name: record.name,
          })),
        );
      })
      .catch(() => setPlacementPartners([]));
  }, [defaultStateId, ppAdmin]);

  useEffect(() => {
    if (!searchForm.stateId) {
      setDistricts([]);
      return;
    }

    void services.requestSvc
      .get<District[]>(`/districts-by-state-id?stateId=${searchForm.stateId}`, {
        withCredentials: true,
      })
      .then((records) => setDistricts(records || []))
      .catch(() => setDistricts([]));
  }, [searchForm.stateId]);

  useEffect(() => {
    void fetchLearners();
  }, [fetchLearners]);

  const handleSearch = () => {
    setPage(1);
    setFilters({ ...searchForm });
  };

  const handleReset = () => {
    const resetFilters = {
      search: "",
      stateId: defaultStateId,
      districtId: "",
      placementPartnerId: "",
      vleCscId: "",
      placementStatus: "",
      placementType: "",
    } as Filters;
    setPage(1);
    setSearchForm(resetFilters);
    setFilters(resetFilters);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Learner Table</h2>
          <p className="text-sm text-gray-500 mt-1">
            Search learner records with VLE and placement partner context.
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-7 gap-3">
          <input
            type="text"
            className="input-primary"
            value={searchForm.search}
            onChange={(event) =>
              setSearchForm((current) => ({
                ...current,
                search: event.target.value,
              }))
            }
            placeholder="Search learner / mobile"
          />
          <select
            className="input-primary"
            value={searchForm.stateId}
            onChange={(event) =>
              setSearchForm((current) => ({
                ...current,
                stateId: event.target.value ? Number(event.target.value) : "",
                districtId: "",
              }))
            }
            disabled={stateAdmin}
          >
            {!stateAdmin && <option value="">All States</option>}
            {states.map((state) => (
              <option key={String(state.id)} value={Number(state.id)}>
                {state.name}
              </option>
            ))}
          </select>
          <select
            className="input-primary"
            value={searchForm.districtId}
            onChange={(event) =>
              setSearchForm((current) => ({
                ...current,
                districtId: event.target.value
                  ? Number(event.target.value)
                  : "",
              }))
            }
          >
            <option value="">All Districts</option>
            {districts.map((district) => (
              <option key={String(district.id)} value={Number(district.id)}>
                {district.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="input-primary"
            value={searchForm.vleCscId}
            onChange={(event) =>
              setSearchForm((current) => ({
                ...current,
                vleCscId: event.target.value,
              }))
            }
            placeholder="VLE CSC ID"
          />
          <select
            className="input-primary"
            value={searchForm.placementPartnerId}
            onChange={(event) =>
              setSearchForm((current) => ({
                ...current,
                placementPartnerId: event.target.value,
              }))
            }
            disabled={ppAdmin}
          >
            <option value="">
              {ppAdmin ? "Assigned Placement Partner" : "All Placement Partners"}
            </option>
            {placementPartners.map((partner) => (
              <option key={partner.id} value={partner.id}>
                {partner.name}
              </option>
            ))}
          </select>
          <select
            className="input-primary"
            value={searchForm.placementStatus}
            onChange={(event) =>
              setSearchForm((current) => ({
                ...current,
                placementStatus: event.target.value as Filters["placementStatus"],
              }))
            }
          >
            <option value="">All Placement Status</option>
            <option value="placed">Placed</option>
            <option value="not_placed">Not Placed</option>
          </select>
          <select
            className="input-primary"
            value={searchForm.placementType}
            onChange={(event) =>
              setSearchForm((current) => ({
                ...current,
                placementType: event.target.value as Filters["placementType"],
              }))
            }
          >
            <option value="">All Placement Types</option>
            <option value="self">Self</option>
            <option value="pp">PP</option>
          </select>
        </div>

        <div className="flex gap-3">
          <button type="button" className="btn-primary" onClick={handleSearch}>
            Search
          </button>
          <button type="button" className="btn-secondary" onClick={handleReset}>
            Reset
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">
              Placed Learners
            </div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {summary.placedLearnerCount}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">
              Self Placed
            </div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {summary.selfPlacedLearnerCount}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">
              Placed By PP
            </div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {summary.placedByPPLearnerCount}
            </div>
          </div>
        </div>
      </div>

      <DataTable<LearnerRecord>
        columns={[
          {
            name: "Learner",
            selector: (row) => row.name,
            width: "16rem",
          },
          {
            name: "Mobile",
            selector: (row) => row.mobileNumber,
            width: "12rem",
          },
          {
            name: "State",
            selector: (row) => row.stateName || "-",
            width: "12rem",
          },
          {
            name: "District",
            selector: (row) => row.districtName || "-",
            width: "12rem",
          },
          {
            name: "VLE",
            selector: (row) => row.vleCscId || "-",
            width: "12rem",
          },
          {
            name: "PP",
            selector: (row) => row.placementPartnerName || "-",
            width: "15rem",
          },
          {
            name: "Skilled",
            selector: (row) => (row.courseCompleted ? "Yes" : "No"),
            width: "8rem",
          },
          {
            name: "Placement Status",
            selector: (row) =>
              row.placementStatus === "placed" ? "Placed" : "Not Placed",
            width: "12rem",
          },
          {
            name: "Placement Type",
            selector: (row) =>
              row.placementType ? row.placementType.toUpperCase() : "-",
            width: "11rem",
          },
          {
            name: "Created",
            selector: (row) =>
              row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "-",
            width: "10rem",
          },
        ]}
        data={rows}
        progressPending={isLoading}
        pagination
        paginationServer
        paginationPerPage={pageSize}
        paginationRowsPerPageOptions={[10, 20, 50]}
        paginationTotalRows={totalRows}
        onChangePage={(nextPage) => setPage(nextPage)}
        onChangeRowsPerPage={(currentRowsPerPage) => {
          setPage(1);
          setPageSize(currentRowsPerPage);
        }}
        className="w-full mt-6"
        noDataComponent="No learners found for the selected filters."
      />
    </div>
  );
}
